import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Box from '@mui/material/Box';
import Checkbox, { type CheckboxProps } from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import {
  DataGridPremium,
  type GridColDef,
  type GridInitialState,
  type GridRenderCellParams,
  type GridRowId,
  type GridRowSelectionModel,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import { type Account, accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { transactionsSelectors } from 'app-components/flux/transactions';
import zeroStateIcon from 'assets/illustrations/Accounts.png';
import AccountMultiSelect from 'components/SimplifiSelects/AccountMultiSelect';
import ZeroStateView from 'components/ZeroStateView';

import { useTheme } from '@mui/material/styles';
import { reduce } from 'app-components/utils/core/immutableHelpers';
import { TypographyCell } from 'components/DataGridCells//TypographyCell';
import { QAmountFieldCell } from 'components/DataGridCells/QAmountFieldCell';
import { getSplitColumn } from 'components/TransactionItemsGrid/TransactionItemsColumns/splitIconColumn';
import { useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React, { useCallback, useState } from 'react';
import type { JSX } from 'react/jsx-runtime';
import { makeStyles } from 'tss-react/mui';

const sx = {
  '& .MuiDataGrid-row': {
    borderRadius: '8px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  minHeight: 300,
  maxHeight: 600,
  overflow: 'hidden',
  '& .MuiDataGrid-main': {
    marginTop: 0, // prevent space for header
  },
  '& .MuiDataGrid-columnHeaders': {
    display: 'none',
  },
};

const initialState: GridInitialState = {
  sorting: {
    sortModel: [{ field: 'date', sort: 'desc' }],
  },
};

const useStyles = makeStyles()((theme) => ({
  accountSelect: {
    marginRight: theme.spacing(1),
  },
}));

type StandardToolbarProps = {
  selectedAccountIds: string[];
  setSelectedAccountIds: (ids: string[]) => void;
  setSelectedIds: (ids: string[]) => void;
  showAccountFilter?: boolean;
  subtitle?: string;
};

const StandardToolbar = (props: StandardToolbarProps) => {
  const { classes } = useStyles();
  return (
    <Box display='flex' alignItems='center' justifyContent='space-between' padding={2} pt={0} width='100%'>
      <Typography variant='bodyRegular'>{props.subtitle ?? 'Select or search for a transaction'}</Typography>
      {props.showAccountFilter && (
        <AccountMultiSelect
          showAllItemsMenu
          selectedItems={props.selectedAccountIds}
          onSelected={(myAccounts: Account[]) => {
            props.setSelectedAccountIds(
              (myAccounts ?? []).reduce<string[]>((acc, account) => {
                if (account.id !== null && account.id !== undefined) {
                  acc.push(account.id);
                }
                return acc;
              }, []),
            );
            props.setSelectedIds([]);
          }}
          accountsSelector={accountsSelectors.getAccountsById}
          className={classes.accountSelect}
        />
      )}
      <GridToolbarQuickFilter
        variant='outlined'
        size='small'
        placeholder='Search transactions'
        sx={{ padding: 0 }}
        debounceMs={350}
      />
    </Box>
  );
};

const ZeroStateGrid = () => {
  return <ZeroStateView icon={zeroStateIcon} primary="We couldn't find any transactions" />;
};

const CheckboxOverride = (props: JSX.IntrinsicAttributes & CheckboxProps) => {
  return <Checkbox {...props} checkedIcon={<CheckRoundedIcon sx={{ color: 'primary.main' }} />} icon={<div />} />;
};

export default function TransactionItemSelect(props: {
  selectedIds: any;
  setSelectedIds: any;
  checkboxSelection?: boolean;
  disableMultipleRowSelection?: boolean;
  showAccountFilter?: boolean;
  txnItemIds: any;
  toolbar?: any;
  subtitle?: string;
}) {
  const {
    selectedIds,
    setSelectedIds,
    checkboxSelection = false,
    disableMultipleRowSelection = true,
    txnItemIds,
    showAccountFilter = true,
    toolbar,
    subtitle,
  } = props;

  const transactionItems = useAppSelector((state) =>
    transactionsSelectors.getTransactionItemsByIdOfTypes(state, transactionsSelectors.txnTypesCash),
  );
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);
  const theme = useTheme();

  const initialAccountIds = React.useMemo(
    () =>
      accountsById.reduce((acc: string[], account: Account) => {
        if (account.id !== null && account.id !== undefined) {
          acc.push(account.id);
        }
        return acc;
      }, []),
    [accountsById],
  );
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(initialAccountIds);

  const columns: GridColDef[] = React.useMemo(() => {
    const isSelected = (id?: GridRowId) => selectedIds.includes(id);
    return [
      {
        field: 'date',
        type: 'date',
        headerName: 'date',
        width: 125,
        renderCell: (params: GridRenderCellParams) => (
          <TypographyCell sx={isSelected(params.id) ? { color: 'primary.main' } : {}}>
            {DateTime.fromJSDate(params.value).toFormat('LLL dd, yyyy')}
          </TypographyCell>
        ),
      },
      {
        field: 'payee',
        headerName: 'payee',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TypographyCell sx={isSelected(params.id) ? { color: 'primary.main' } : {}}>{params.value}</TypographyCell>
        ),
      },
      getSplitColumn(),
      {
        field: 'category',
        headerName: 'Category',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TypographyCell sx={isSelected(params.id) ? { color: 'primary.main' } : {}}>
            {params.formattedValue.split(':').pop()}
          </TypographyCell>
        ),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 160,
        align: 'right',
        renderCell: (params: GridRenderCellParams) => (
          <QAmountFieldCell
            {...params}
            sx={isSelected(params.id) ? { color: `${theme.palette.primary.main} !important` } : {}}
            value={params.value}
            showColor='positive'
            expectedSign='negative'
          />
        ),
      },
    ];
  }, [selectedIds, theme]);

  const rows = React.useMemo(() => {
    type TransactionRow = {
      id: string;
      date: Date;
      payee: string;
      split: boolean;
      category: string;
      amount: number;
    };

    return reduce<string, TransactionRow[]>(txnItemIds, [], (acc, id) => {
      const item = transactionItems.get(id);

      if (
        !item ||
        !item.item ||
        (selectedAccountIds.length > 0 && !selectedAccountIds.includes(item.item.accountId ?? ''))
      ) {
        return acc;
      }
      acc.push({
        id,
        date: DateTime.fromISO(item.item.postedOn ?? '').toJSDate(),
        payee: item.item.payee ?? 'N/A',
        split: Boolean(item.item.split),
        category: chartOfAccountsUtils.getCoaString(item.item.coa, categoriesById, accountsById, false),
        amount: item.item.amount ?? 0,
      });

      return acc;
    });
  }, [transactionItems, txnItemIds, categoriesById, accountsById, selectedAccountIds]);

  const handleSelect = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      setSelectedIds(newRowSelectionModel);
    },
    [setSelectedIds],
  );

  const slots = React.useMemo(
    () => ({
      baseCheckbox: disableMultipleRowSelection ? CheckboxOverride : undefined,
      toolbar: () =>
        toolbar ?? (
          <StandardToolbar
            selectedAccountIds={selectedAccountIds}
            setSelectedAccountIds={setSelectedAccountIds}
            showAccountFilter={showAccountFilter}
            setSelectedIds={setSelectedIds}
            subtitle={subtitle}
          />
        ),
      noRowsOverlay: () => <ZeroStateGrid />,
    }),
    [
      toolbar,
      selectedAccountIds,
      setSelectedAccountIds,
      subtitle,
      showAccountFilter,
      setSelectedIds,
      disableMultipleRowSelection,
    ],
  );

  return (
    <DataGridPremium
      ignoreDiacritics
      hideFooter
      checkboxSelection={checkboxSelection}
      disableMultipleRowSelection={disableMultipleRowSelection}
      columns={columns}
      rows={rows}
      onRowSelectionModelChange={handleSelect}
      rowSelectionModel={selectedIds}
      columnHeaderHeight={0}
      slots={slots}
      sx={sx}
      initialState={initialState}
    />
  );
}
